import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import { Button } from "@/components/ui/button";
import { UsersThree, ChatCircleDots, ArrowRight, X } from "@phosphor-icons/react";

// Guidance-only screen — deliberately NOT a chat, booking or contact form.
// Reachable from the "worried" quiz-result primary CTA.
export default function ParlarneConQualcuno() {
  const { t } = useApp();
  const navigate = useNavigate();
  const copy = t.talkTo;

  return (
    <div data-testid="talk-to-someone" className="max-w-2xl mx-auto px-5 py-10 space-y-10">
      {/* Exit control */}
      <div className="flex justify-end -mb-4">
        <button
          data-testid="talk-to-exit"
          aria-label={copy.exitCta}
          onClick={() => navigate("/")}
          className="w-10 h-10 rounded-full border border-border bg-surface-card text-ink-soft hover:text-ink hover:bg-surface-alt transition-colors flex items-center justify-center"
        >
          <X size={18} weight="bold" />
        </button>
      </div>

      {/* Hero — calm mascot + eyebrow + title + intro */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center space-y-4"
      >
        <div className="flex justify-center">
          <MascotPlaceholder src="/assets/boardy-calmo.png" />
        </div>
        <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
          {copy.eyebrow}
        </p>
        <h1
          data-testid="talk-to-title"
          className="font-display font-black text-3xl sm:text-4xl text-ink leading-tight tracking-tight"
        >
          {copy.title}
        </h1>
        <p className="text-ink-soft text-base sm:text-lg leading-relaxed font-sans max-w-xl mx-auto">
          {copy.intro}
        </p>
      </motion.div>

      {/* People to involve */}
      <section
        data-testid="talk-to-people"
        className="bg-surface-card rounded-3xl border border-border p-6 sm:p-8 shadow-[0_4px_20px_rgba(61,64,91,0.06)]"
      >
        <div className="flex items-center gap-2 mb-5">
          <UsersThree size={22} weight="duotone" className="text-sage" />
          <h2 className="font-display font-extrabold text-xl text-ink tracking-tight">
            {copy.peopleTitle}
          </h2>
        </div>
        <ul className="space-y-4">
          {copy.people.map((p, i) => (
            <li
              key={i}
              data-testid={`talk-to-person-${i}`}
              className="flex gap-4 items-start"
            >
              <div className="mt-1 w-2 h-2 rounded-full bg-terracotta shrink-0" aria-hidden="true" />
              <div>
                <div className="font-sans font-bold text-ink">{p.title}</div>
                <div className="text-sm text-ink-soft leading-relaxed mt-0.5">{p.body}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Conversation openers */}
      <section
        data-testid="talk-to-openers"
        className="bg-sand/20 rounded-3xl border border-sand/50 p-6 sm:p-8"
      >
        <div className="flex items-center gap-2 mb-3">
          <ChatCircleDots size={22} weight="duotone" className="text-terracotta" />
          <h2 className="font-display font-extrabold text-xl text-ink tracking-tight">
            {copy.openersTitle}
          </h2>
        </div>
        <p className="text-sm text-ink-soft italic leading-relaxed mb-5">
          {copy.openersHint}
        </p>
        <ul className="space-y-3">
          {copy.openers.map((line, i) => (
            <li
              key={i}
              data-testid={`talk-to-opener-${i}`}
              className="rounded-2xl bg-surface-card border border-border/70 p-4 text-ink font-sans leading-relaxed"
            >
              <span className="text-terracotta font-bold mr-1.5" aria-hidden="true">“</span>
              {line}
              <span className="text-terracotta font-bold ml-1" aria-hidden="true">”</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Cards as a base for the conversation */}
      <section
        data-testid="talk-to-back-to-cards"
        className="rounded-3xl border border-border bg-surface-card p-6 sm:p-8"
      >
        <h2 className="font-display font-extrabold text-xl text-ink tracking-tight mb-2">
          {copy.backToCardsTitle}
        </h2>
        <p className="text-sm text-ink-soft leading-relaxed mb-5">
          {copy.backToCardsBody}
        </p>
        <Button
          data-testid="talk-to-cards-cta"
          onClick={() => navigate("/esplora")}
          className="rounded-full h-12 px-6 bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold w-full sm:w-auto"
        >
          {copy.backToCardsCta}
          <ArrowRight size={16} weight="bold" className="ml-2" />
        </Button>
      </section>
    </div>
  );
}
